import React, {useEffect} from 'react';
import {FormattedMessage} from 'react-intl';
import moment from 'moment/moment';
import {FormikProvider} from 'formik';
import Spinner from '../../../_common/components/Spinner';
import {useOldInspectionDetailsContext} from '../OldInspectionsDetailsContext';
import FieldSelect from '../../../_common/components/FieldSelect';
import {StyledFieldDatepicker} from '../../../_common/components/FieldDatepicker';
import Unit from '../../../_common/components/Unit';
import FieldInput from '../../../_common/components/FieldInput';
import {
  divideThousandsInteger,
  formatStringToNumber, limitFloatValueLength,
  limitIntegerValueLength
} from '../../../_common/utils/changeInputValue';
import {filterOptions} from '../../../_common/utils/options';
import {useCitiesContext, useStreetsContext} from '../../../_common/components/CitiesAndStreets';
import FieldCheckbox from '../../../_common/components/FieldCheckbox';
import FieldFindCustomers from '../../../_common/components/FieldFindCustomers';
import ButtonAction from '../../../_common/components/ButtonAction';

export function TabDetails() {
  const {
    fieldNames,
    formik,
    loading,
    editOldInspection
  } = useOldInspectionDetailsContext();
  const {cities} = useCitiesContext();
  const {streets, updateStreets} = useStreetsContext();
  const {values, setFieldValue} = formik;
  const {cityID} = values;
  useEffect(() => {
    if (cityID) {
      updateStreets(cityID);
    }
  }, [cityID]);
  const prefix = 'inspection-details-page';
  const commonProps = {
    customContainerStyles: 'mb-4'
  };

  return (
    <FormikProvider value={formik}>
      {(!loading)
        ? (
          <>
            <div className={`w-100 d-flex justify-content-between`}>
              <Unit
                text={<FormattedMessage id={`${prefix}-section-generalInfo`} defaultMessage="General info"/>}
                icon={'/media/common/Description_orange.svg'}
                customClassnames={'w-100 mx-2'}
              >
                <div className={'d-flex justify-content-between'}>
                  <div>
                    <FieldInput
                      fieldId={fieldNames.gush}
                      value={values.gush}
                      onChange={limitIntegerValueLength(6)}
                      label={
                        <div>
                          <span className="text-danger">*</span>
                          <FormattedMessage id={`${prefix}-gush-label`} defaultMessage="Gush"/>
                        </div>
                      }
                      icon={'/media/common/Map_primary.svg'}
                      {...commonProps}
                    />
                    <FieldInput
                      fieldId={fieldNames.helka}
                      value={values.helka}
                      onChange={limitIntegerValueLength(6)}
                      label={
                        <div className={'d-flex align-items-center'}>
                          <span className="text-danger">*</span>
                          <FormattedMessage id={`${prefix}-helka-label`} defaultMessage="Helka"/>
                        </div>
                      }
                      icon={'/media/common/Map_primary.svg'}
                      {...commonProps}
                    />
                    <FieldInput
                      fieldId={fieldNames.ttHelka}
                      value={values.ttHelka}
                      onChange={limitIntegerValueLength(4)}
                      label={<FormattedMessage id={`${prefix}-ttHelka-label`} defaultMessage="ttHelka"/>}
                      icon={'/media/common/Map_primary.svg'}
                      {...commonProps}
                    />
                    <FieldSelect
                      fieldId={fieldNames.cityID}
                      value={filterOptions(values.cityID)(cities)}
                      onChange={(value) => {
                        setFieldValue(fieldNames.cityID, value);
                        setFieldValue(fieldNames.streetID, '');
                      }}
                      options={cities}
                      isSearchable={true}
                      isAsync={true}
                      label={<FormattedMessage id="filter-placeholder-city" defaultMessage="City"/>}
                      {...commonProps}
                    />
                    <FieldSelect
                      fieldId={fieldNames.streetID}
                      value={streets ? filterOptions(values.streetID)(streets) : null}
                      onChange={(value) => setFieldValue(fieldNames.streetID, value)}
                      options={streets || []}
                      isSearchable={true}
                      isAsync={true}
                      defaultOptions={true}
                      isDisabled={!values.cityID}
                      label={<FormattedMessage id="filter-placeholder-street" defaultMessage="Street"/>}
                      {...commonProps}
                    />
                  </div>
                  <div className={'ml-4'}>
                    <FieldInput
                      fieldId={fieldNames.houseNumber}
                      value={values.houseNumber}
                      label={<FormattedMessage id={`${prefix}-houseNumber-label`} defaultMessage="houseNumber"/>}
                      icon={'/media/common/Home_primary.svg'}
                      {...commonProps}
                    />
                    <StyledFieldDatepicker
                      value={values[fieldNames.officialDate]}
                      onChange={date => setFieldValue(fieldNames.officialDate, moment(date).format('YYYY-MM-DD'))}
                      placeholder={moment(values[fieldNames.officialDate]).format('DD-MM-YYYY')}
                      label={<FormattedMessage id={`${prefix}-date-label`} defaultMessage="Date"/>}
                      {...commonProps}
                    />
                    <FieldInput
                      fieldId="averageSqmPrice"
                      value={values.averageSqmPrice ? parseFloat(values.averageSqmPrice).toLocaleString() : ''}
                      onChange={limitIntegerValueLength(8)}
                      label={<FormattedMessage id={`${prefix}-pricePerSQM-label`} defaultMessage="Price per SQM"/>}
                      icon={'/media/common/Money_primary.svg'}
                      {...commonProps}
                    />
                    <FieldInput
                      fieldId={fieldNames.estimatePriceFinal}
                      value={divideThousandsInteger(values.estimatePriceFinal)}
                      onChange={formatStringToNumber}
                      label={<FormattedMessage id={`${prefix}-price-label`} defaultMessage="Price"/>}
                      icon={'/media/common/Money_primary.svg'}
                      {...commonProps}
                    />
                  </div>
                </div>
              </Unit>
              <Unit
                text={<FormattedMessage id={`${prefix}-section-propertyInfo`} defaultMessage="Property info"/>}
                icon={'/media/common/Home_primary.svg'}
                customClassnames={'w-100 mx-2'}
              >
                <div className={'d-flex justify-content-between'}>
                  <div>
                    <FieldInput
                      fieldId={fieldNames.landRegisteredLotArea}
                      value={values.landRegisteredLotArea}
                      onChange={limitFloatValueLength(7)}
                      label={<FormattedMessage id={`${prefix}-lotArea-label`} defaultMessage="Lot area"/>}
                      icon={'/media/common/Lock_primary.svg'}
                      {...commonProps}
                    />
                    <FieldInput
                      fieldId={fieldNames.houseRegisteredArea}
                      value={values.houseRegisteredArea}
                      onChange={limitFloatValueLength(7)}
                      label={<FormattedMessage id={`${prefix}-registeredArea-label`} defaultMessage="Registered area"/>}
                      icon={'/media/common/Map_primary.svg'}
                      {...commonProps}
                    />
                    <FieldInput
                      fieldId={fieldNames.houseRoomCount}
                      value={values.houseRoomCount}
                      onChange={limitIntegerValueLength(2)}
                      label={<FormattedMessage id={`${prefix}-roomCount-label`} defaultMessage="Room count"/>}
                      icon={'/media/common/Rooms_primary.svg'}
                      {...commonProps}
                    />
                  </div>
                  <div className={'ml-4'}>
                    <FieldInput
                      fieldId={fieldNames.floor}
                      value={values.floor}
                      onChange={limitIntegerValueLength(2)}
                      label={<FormattedMessage id={`${prefix}-floor-label`} defaultMessage="Floor"/>}
                      icon={'/media/common/Number_primary.svg'}
                      {...commonProps}
                    />
                    <FieldInput
                      fieldId={fieldNames.floorCount}
                      value={values.floorCount}
                      onChange={limitIntegerValueLength(2)}
                      label={<FormattedMessage id={`${prefix}-floorCount-label`} defaultMessage="Floor count"/>}
                      icon={'/media/common/Layers_primary.svg'}
                      {...commonProps}
                    />
                    <FieldInput
                      fieldId={fieldNames.buildingAge}
                      value={values.buildingAge}
                      onChange={limitIntegerValueLength(4)}
                      label={<FormattedMessage id={`${prefix}-buildYear-label`} defaultMessage="Build year"/>}
                      icon={'/media/common/Home_primary.svg'}
                      {...commonProps}
                    />
                  </div>
                </div>
              </Unit>
              <div className={'w-100 mx-2 d-flex flex-column align-items-center'}>
                <Unit
                  text={<FormattedMessage id={`${prefix}-section-moreInfo`} defaultMessage="More info"/>}
                  icon={'/media/common/Layers_green.svg'}
                  customClassnames={'w-100 mb-8'}
                >
                  <div className={'d-flex justify-content-between pt-4'}>
                    <FieldCheckbox
                      fieldId={fieldNames.elevator}
                      label={<FormattedMessage id={`${prefix}-elevator-label`} defaultMessage="Elevator"/>}
                      value={values.elevator}
                      customContainerStyles={'mx-2'}
                    />
                    <FieldCheckbox
                      fieldId={fieldNames.parking}
                      label={<FormattedMessage id={`${prefix}-parking-label`} defaultMessage="Parking"/>}
                      value={values.parking}
                      customContainerStyles={'mx-2'}
                    />
                    <FieldCheckbox
                      fieldId={fieldNames.yard}
                      label={<FormattedMessage id={`${prefix}-yard-label`} defaultMessage="Yard"/>}
                      value={values.yard}
                      customContainerStyles={'mx-2'}
                    />
                    <FieldCheckbox
                      fieldId={fieldNames.storage}
                      label={<FormattedMessage id={`${prefix}-storage-label`} defaultMessage="Storage"/>}
                      value={values.storage}
                      customContainerStyles={'mx-2'}
                    />
                    <FieldCheckbox
                      fieldId={fieldNames.roof}
                      label={<FormattedMessage id={`${prefix}-roof-label`} defaultMessage="Roof"/>}
                      value={values.roof}
                      customContainerStyles={'mx-2'}
                    />
                  </div>
                </Unit>
                <Unit
                  text={<FormattedMessage id={`${prefix}-section-customers`} defaultMessage="Customers"/>}
                  icon={'/media/common/Group_pink.svg'}
                  customClassnames={'w-100'}
                >
                  <FieldFindCustomers
                    name={fieldNames.customers}
                    formik={{values, setFieldValue}}
                    readOnlyMode={true}
                  />
                </Unit>
              </div>
            </div>
            <ButtonAction
              useIcon={'/media/common/Check-in-circle_white.svg'}
              label={<FormattedMessage id="submit" defaultMessage="Submit"/>}
              onClick={() => editOldInspection(formik.values)}
              disabled={!(formik.dirty && formik.isValid)}
              customClassnames={'d-block mx-auto mt-10'}
            />
          </>
        )
        : (
          <div className="w-100 h-100 d-flex justify-content-center align-items-center">
            <Spinner/>
          </div>
        )
      }
    </FormikProvider>
  );
}

export default TabDetails;
